/**
 * CoverageStrip — the one run-summary component (Results v2 §4, build step 2).
 *
 * DC-1746: it now carries the ARC ROLL-UP as well, and the two live in ONE
 * component deliberately. §2.2 rules that "the headline is unrepresentable
 * without its coverage... a consumer that can obtain the number without the
 * coverage is a defect, not a convenience." Physical coupling is the only
 * version of that rule a screenshot cannot break: there is no arrangement
 * of this page in which the band travels without what it covers.
 *
 * WHAT IS RENDERED IS A BAND AND A 0-5 SCORE, NEVER A PERCENTAGE. ARC's
 * output is a band name on a 0-5 scale; a percentage would imply a complete
 * denominator we do not have, which is the exact lie the aggregation
 * decision exists to prevent. The no-percentage regression still holds over
 * this whole container and must keep holding.
 *
 * EVERYTHING HERE IS SERVER-COMPUTED. The client does not band, round,
 * re-derive or re-scale anything — it prints what the record stored, and a
 * refusal by its name.
 *
 * Replaces the counts row on Results. Count-based only: answered n of N in
 * proportional per-test segments, the open region on the track with a dashed
 * neutral border (track alone is imperceptible against the card), and the
 * `collector_error` reliability line the old counts row omitted entirely —
 * stated separately, never pooled into answered or awaiting.
 *
 * The numbers live INSIDE their segments so a screenshot cannot carry
 * "8 answered" without the open region travelling with it. No percentage, no
 * score, no gauge: a radial form implies a complete denominator, which is the
 * exact lie the aggregation decision (engine doc §2.2) exists to prevent.
 *
 * The measured/attested split is carried textually beneath the strip — the
 * API serves basis totals, not a per-outcome cross-tab, so texturing the
 * outcome segments by basis would assert a join no response contains
 * (§10.1's sanctioned fallback).
 *
 * `counts = null` renders the fully open no-run strip (§7): the in-scope
 * count is unknown until a run — never a zeroed strip.
 *
 * ARC IP boundary: counts and outcome identifiers only.
 */

import React from 'react';
import type { OutcomeCounts, RunRollup } from './assessmentTypes';

/** Micro-scale redundant outcome marks (§3.4) — colour never stands alone. */
const SEGMENTS = [
  { key: 'satisfied', tone: 'good', mark: null },
  { key: 'marginal', tone: 'soft', mark: '~' },
  { key: 'notSatisfied', tone: 'hard', mark: '×' },
] as const;

function needsAPerson(n: number): string {
  return `${n} ${n === 1 ? 'needs' : 'need'} a person`;
}

function needEvidence(n: number): string {
  return `${n} ${n === 1 ? 'needs' : 'need'} evidence`;
}

/** Refusal reasons in a reader's words. Each names what to DO about it. */
const REFUSAL_TEXT: Record<string, string> = {
  no_weight_config:
    'no ARC weight configuration is in force, and these values are never defaulted',
  incomplete_weight_config:
    'the ARC weight configuration in force is incomplete',
  no_materiality_tier:
    'this use case carries no materiality tier — it is inherited from the firm’s own model-risk inventory, and there is no default',
  indeterminate_ceiling:
    'the control-score scale could not be read from the catalog this run judged',
  indeterminate_control_severity:
    'a control’s tests disagree on severity, so there is no weight to apply to it',
  no_assessable_control:
    'no control on this use case has an answered finding — coverage of nothing is not a score of zero',
};

const BAND_LABEL: Record<string, string> = {
  critical: 'Critical',
  elevated: 'Elevated',
  monitor: 'Monitor',
  effective: 'Effective',
};

/**
 * The roll-up half. Rendered above the coverage and inside the same
 * container, so the two cannot be separated.
 */
const RollupBand: React.FC<{ rollup: RunRollup }> = ({ rollup }) => {
  if (rollup.status !== 'computed' || rollup.useCaseRisk === null) {
    const reason =
      rollup.status === 'not_attempted'
        ? 'this run was assessed under a method version that computed no roll-up'
        : (REFUSAL_TEXT[rollup.refusalReason ?? ''] ??
          'the roll-up could not be computed');
    // The detail names WHAT was indeterminate — which controls, which test
    // codes, which configuration keys. The combiner computes it, the record
    // stores it and the DTO carries it, so dropping it here left a reader
    // with a reason they could not act on: "a control's tests disagree on
    // severity" is true and useless without the control. On the real catalog
    // it names CS1 through CS5.
    const detail = rollup.refusalDetail?.trim();

    return (
      <div className="uc-rollup uc-rollup-refused" data-testid="rollup-refused">
        <span className="uc-rollup-band-none">No ARC band</span>
        <p className="uc-rollup-reason" data-testid="rollup-refusal-reason">
          No roll-up is shown: {reason}. Compass does not compute a number ARC has not
          specified.
        </p>
        {detail && (
          <p className="uc-rollup-detail" data-testid="rollup-refusal-detail">
            {detail}
          </p>
        )}
      </div>
    );
  }

  const band = rollup.statusBand ?? rollup.riskBand;
  const gated = rollup.criticalGateFired === true;

  return (
    <div className="uc-rollup" data-testid="rollup-band">
      <div className="uc-rollup-head">
        <span
          className={`uc-rollup-chip uc-rollup-chip-${band}`}
          data-testid="rollup-status-band"
        >
          {BAND_LABEL[band ?? ''] ?? band}
        </span>
        {/* A 0-5 score with its scale attached. Never a percentage. */}
        <span className="uc-rollup-score" data-testid="rollup-score">
          {rollup.useCaseRisk.toFixed(2)} of {rollup.riskScaleMax ?? '?'}
        </span>
      </div>

      {gated && (
        <p className="uc-rollup-gate" data-testid="rollup-gate">
          A hard gate failed, so the status is Critical regardless of the weighted band
          {rollup.riskBand ? ` (${BAND_LABEL[rollup.riskBand] ?? rollup.riskBand})` : ''} and
          regardless of the materiality tier.
        </p>
      )}

      <p className="uc-rollup-basis" data-testid="rollup-basis">
        {rollup.materialityTier ?? 'tier unstated'}
        {rollup.tierMultiplier !== null ? ` ×${rollup.tierMultiplier}` : ''}
        {' · '}
        {rollup.assessedControlCount ?? 0} of{' '}
        {(rollup.assessedControlCount ?? 0) + (rollup.unassessedControlCount ?? 0)} controls
        assessed
        {rollup.systemicUpliftApplied !== null
          ? ` · systemic uplift ×${rollup.systemicUpliftApplied}`
          : ''}
      </p>

      {/* ZERO IS THE DANGEROUS VALUE, so it is stated rather than omitted:
          with no binding the four named hard gates can never fire. */}
      {rollup.hardGateBindingCount === 0 && (
        <p className="uc-rollup-caveat" data-testid="rollup-no-hard-gates">
          No hard gates are bound, so the four named gates could not fire on this run.
        </p>
      )}

      {/* Override capture does not exist yet. Saying "0 overrides" would
          claim a capability we do not have; stating the absence does not. */}
      <p className="uc-rollup-caveat" data-testid="rollup-overrides">
        Overrides cannot yet be recorded against this band.
      </p>
    </div>
  );
};

export const CoverageStrip: React.FC<{
  counts: OutcomeCounts | null;
  rollup?: RunRollup | null;
}> = ({ counts, rollup = null }) => {
  if (counts === null) {
    // §7 no-run state: fully open at full width, in-scope count unknown.
    return (
      <div className="uc-coverage uc-coverage-no-run" data-testid="coverage-strip-open">
        <div className="uc-coverage-captions">
          <span>coverage — no run yet</span>
        </div>
        <div className="uc-coverage-bar">
          <div className="uc-coverage-region-awaiting uc-coverage-region-unknown">
            the in-scope count is unknown until a run
          </div>
        </div>
      </div>
    );
  }

  const answered = counts.satisfied + counts.marginal + counts.notSatisfied;
  const awaiting = counts.attestationRequired + counts.notAssessable;
  const errors = counts.collectorError;

  const proseParts = [
    `${answered} of ${counts.total} in-scope tests answered — ` +
      `${counts.measured} measured, ${counts.attested} attested.`,
    `${awaiting} await: ${counts.attestationRequired} a person, ${counts.notAssessable} evidence.`,
  ];
  if (errors > 0) {
    proseParts.push(
      `${errors} collector ${errors === 1 ? 'error' : 'errors'} — a reliability gap, not an answer.`
    );
  }
  // DC-1746: this line used to read "No aggregate score is shown". That was
  // true only while the combiner was partner-gated on five R6 values; they
  // arrived on 2026-08-22 and the roll-up is computed and stored server-side
  // now. What the line says instead is where the band comes from and what it
  // is NOT — because "0-5 band, never a percentage" is the constraint a
  // reader most needs stated.
  proseParts.push(
    rollup !== null && rollup.status === 'computed'
      ? 'The ARC band above is computed server-side from the framework owner’s R6 model and is a 0–' +
          `${rollup.riskScaleMax ?? 5} scale with a band name — never a percentage.`
      : 'No ARC band is shown for this run, and no number stands in for one.'
  );

  return (
    <div className="uc-coverage" data-testid="coverage-strip">
      {rollup !== null && <RollupBand rollup={rollup} />}

      <div className="uc-coverage-captions">
        <span data-testid="coverage-answered-caption">
          answered {answered} of {counts.total}
        </span>
        <span data-testid="coverage-awaiting-caption">awaiting {awaiting}</span>
      </div>

      <div
        className="uc-coverage-bar"
        role="img"
        aria-label={
          `${answered} of ${counts.total} in-scope tests answered; ${awaiting} awaiting; ` +
          `${errors} collector ${errors === 1 ? 'error' : 'errors'}`
        }
      >
        {answered > 0 && (
          <div className="uc-coverage-region-answered" style={{ flexGrow: answered }}>
            {SEGMENTS.map(({ key, tone, mark }) => {
              const n = counts[key];
              return n > 0 ? (
                <div
                  key={key}
                  className={`uc-coverage-seg uc-coverage-seg-${tone}`}
                  style={{ flexGrow: n }}
                  data-testid={`strip-seg-${key}`}
                >
                  {mark && (
                    <span className="uc-coverage-mark" aria-hidden="true">
                      {mark}
                    </span>
                  )}
                  <span className="uc-coverage-seg-n">{n}</span>
                </div>
              ) : null;
            })}
          </div>
        )}
        {awaiting > 0 && (
          <div
            className="uc-coverage-region-awaiting"
            style={{ flexGrow: awaiting }}
            data-testid="strip-region-awaiting"
          >
            {needsAPerson(counts.attestationRequired)} · {needEvidence(counts.notAssessable)}
          </div>
        )}
        {errors > 0 && (
          <div
            className="uc-coverage-region-errors"
            style={{ flexGrow: errors }}
            data-testid="strip-seg-errors"
          >
            {errors}
          </div>
        )}
      </div>

      <div className="uc-coverage-meta">
        <span data-testid="coverage-basis">
          {counts.measured} measured · {counts.attested} attested
        </span>
        <span
          className={errors > 0 ? 'uc-coverage-errors-n is-hard' : 'uc-coverage-errors-n'}
          data-testid="coverage-collector-errors"
        >
          collector errors: {errors}
        </span>
      </div>

      <p className="uc-coverage-prose" data-testid="coverage-prose">
        {proseParts.join(' ')}
      </p>
    </div>
  );
};

export default CoverageStrip;
